import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { GRID_PAGE_SIZE } from '@models-global/constants';
import { ModelPagination } from '@models-global/model-pagination';
import { IReportDownload } from '@module-report/models/report-download.interface';
import { ReportHttpService } from '@module-report/services/report-http.service';
import * as actions from '@module-report/store/report.actions';

export interface IData<T> {
  pending: boolean;
  data: T;
}

export interface IDataPage<T> extends ModelPagination<T> {
  pending: boolean;
  data: T;
}

export interface IReportState {
  downloads: IDataPage<IReportDownload[]>;
  reportDownload: IData<IReportDownload>;
  reportGenerate: IData<IReportDownload>;
}

const DataDefault: IData<any> = {
  pending: false,
  data: undefined
};

const DataPageDefault: IDataPage<any> = {
  skip: 0,
  take: GRID_PAGE_SIZE,
  total: 0,
  pending: false,
  data: []
};

@State<IReportState>({
  name: 'ReportState',
  defaults: {
    downloads: DataPageDefault,
    reportDownload: DataDefault,
    reportGenerate: DataDefault
  }
})
@Injectable()
export class ReportState {
  @Selector()
  static downloads(state: IReportState): IData<IReportDownload[]> {
    return state.downloads;
  }

  @Selector()
  static reportDownload(state: IReportState): IData<any> {
    return state.reportDownload;
  }

  @Selector()
  static reportGenerate(state: IReportState): IData<any> {
    return state.reportGenerate;
  }

  constructor(private _reportService: ReportHttpService) {}

  @Action(actions.GetReportDownloadList, { cancelUncompleted: true })
  onGetReportDownloadList(
    { patchState, getState }: StateContext<IReportState>,
    { params }: actions.GetReportDownloadList
  ) {
    patchState({
      downloads: {
        ...getState().downloads,
        pending: true
      }
    });

    return this._reportService.getReportDownloadList(params).pipe(
      tap((res) =>
        patchState({
          downloads: {
            ...res,
            pending: false
          }
        })
      ),
      catchError((e) => {
        patchState({
          downloads: {
            ...getState().downloads,
            pending: false
          }
        });
        return throwError(e);
      })
    );
  }

  @Action(actions.DownloadReport)
  onDownloadReport(
    { patchState, getState }: StateContext<IReportState>,
    { reportId }: actions.DownloadReport
  ) {
    patchState({
      reportDownload: {
        ...getState().reportDownload,
        pending: true
      }
    });

    return this._reportService.downloadGeneratedReport(reportId).pipe(
      tap((res) =>
        patchState({
          reportDownload: {
            data: res,
            pending: false
          }
        })
      ),
      catchError((e) => {
        patchState({
          reportDownload: {
            ...getState().reportDownload,
            pending: false
          }
        });
        return throwError(e);
      })
    );
  }

  @Action(actions.GenerateReport)
  onGenerateReport(
    { patchState, getState }: StateContext<IReportState>,
    { params }: actions.GenerateReport
  ) {
    patchState({
      reportGenerate: {
        ...getState().reportGenerate,
        pending: true
      }
    });

    return this._reportService.generateReport(params).pipe(
      tap((res) =>
        patchState({
          reportGenerate: {
            data: res,
            pending: false
          }
        })
      ),
      catchError((e) => {
        patchState({
          reportGenerate: {
            ...getState().reportGenerate,
            pending: false
          }
        });
        return throwError(e);
      })
    );
  }
}
